import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BaseComponent } from '@core/components/base-component';
import { DepartmentEstimateHelper } from '@core/helpers/department-estimate.helper';
import { StatusEnum } from '@enums/Status';
import { Estimate } from '@interfaces/Estimate';
import { UserDto } from '@interfaces/UserDto';
import { TranslateService } from '@ngx-translate/core';
import { EstimateSettingsService } from '@shared/services/estimate-settings.service';
import { EstimateStructureLinksService } from '@shared/services/estimate-structure-links.service';
import { EstimateService } from '@shared/services/estimate.service';
import { LoadingService } from '@shared/services/loading.service';
import { UserService } from '@shared/services/user.service';
import { ConfirmationService } from 'primeng/api';
import { Table } from 'primeng/table';
import { Subscription, firstValueFrom } from 'rxjs';
import { DetailedAssociationDialogComponent } from './detailed-association-dialog/detailed-association-dialog.component';
import { DepartmentRow } from './models/department.row';

@Component({
  selector: 'pop-department-modal',
  templateUrl: './department-modal.component.html',
  styleUrls: ['./department-modal.component.less']
})
export class DepartmentModalComponent extends BaseComponent implements OnInit {
  private currentEstimate: Estimate;
  public isGroupApproved = true;
  public isStatusTypeNew: boolean;
  public isEmptyTree = true;

  public departmentLabel: string;
  public rows: DepartmentRow[];
  public constructor(
    private estimateService: EstimateService,
    private userService: UserService,
    private settingsService: EstimateSettingsService,
    private loadingService: LoadingService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private estimateStructureLinksService: EstimateStructureLinksService,
    private dialogService: MatDialog
  ) {
    super();
  }

  public async ngOnInit() {
    this.subscribe(
      this.estimateService.observeIsCurrentEstimateGroupApproved().subscribe(isGroupApproved => {
        this.isGroupApproved = isGroupApproved;
      })
    );
    this.subscribe(
      this.estimateService.observeCurrentEstimate().subscribe(estimate => {
        this.currentEstimate = estimate;
        this.isStatusTypeNew = estimate.status === StatusEnum.New;
        this.isEmptyTree = estimate.structures.length === 0;
      })
    );

    const user = await this.userService.getUserAsync();
    const config = await this.settingsService.getEstimateConfigurationAsync(false);
    const restrictedCompanies = this.settingsService.getRestrictedCompanies(config);

    await this.loadingService.run(async () => {
      await firstValueFrom(this.estimateService.observeCurrentEstimate());
      this.departmentLabel = await this.settingsService.getDepartmentLabel();

      this.currentEstimate.departmentEstimates.forEach(de => {
        DepartmentEstimateHelper.SetIsAssociatedToAllStructures(de, this.currentEstimate);
      });

      await this.createRows(user, restrictedCompanies);
    });

    this.subscribe(
      new Subscription(() => {
        this.estimateService.saveChanges().subscribe();
      })
    );
  }

  private async createRows(user: UserDto, restrictedCompanies: string[]) {
    const departments = await firstValueFrom(this.estimateService.getDepartments(user, restrictedCompanies));

    this.rows = departments.map(department => {
      const deptEstim = this.currentEstimate.departmentEstimates.find(d => d.departmentId === department.id);

      const row = new DepartmentRow(department, deptEstim);
      return row;
    });
  }

  public openDetailedStructureAssociation(row: DepartmentRow) {
    this.dialogService.open(DetailedAssociationDialogComponent, {
      width: '750px',
      height: '100%',
      panelClass: 'custom-dialog-container',
      data: {
        row: row
      }
    });
  }

  public async toggleDepartment(event: MouseEvent, row: DepartmentRow) {
    event.preventDefault();

    if (row.departmentEstimate) {
      //remove one
      if (await this.confirmDelete()) {
        await this.loadingService.run(() => this.removeDepartmentEstimate(row));
      }
      return;
    }

    // add one
    await this.loadingService.run(() => this.addDepartmentEstimate(row));
  }

  private async removeDepartmentEstimate(row: DepartmentRow) {
    const departmentEstimate = row.departmentEstimate;
    row.departmentEstimate = undefined;

    await this.estimateService.deleteDepartmentEstimate(departmentEstimate);
  }

  private async addDepartmentEstimate(row: DepartmentRow) {
    row.departmentEstimate = this.estimateService.createDepartmentEstimate(
      this.currentEstimate.id,
      row.department.id,
      true,
      undefined
    );

    await this.estimateService.saveChangesAsync();
    await this.associateDeptToAllStructures(row);
  }

  public async toggleIsAssociatedToAllStructures(row: DepartmentRow, event: MouseEvent) {
    event.preventDefault();
    if (row.departmentEstimate.isAssociatedToAllStructures) {
      if (await this.confirmDelete()) {
        await this.loadingService.run(() => this.disassociateDeptToAllStructures(row));
      }
      return;
    }

    await this.loadingService.run(() => this.associateDeptToAllStructures(row));
  }

  private async disassociateDeptToAllStructures(row: DepartmentRow) {
    await this.estimateService.deleteEstimateLinesByDeptEstim(row.departmentEstimate.id);

    const lines = row.departmentEstimate.estimateLines.slice();
    row.departmentEstimate.isAssociatedToAllStructures = false;

    await this.estimateService.detachEntities(lines);
    await this.estimateService.saveChangesAsync();
  }

  private async associateDeptToAllStructures(row: DepartmentRow) {
    const departmentEstimate = row.departmentEstimate;
    const structures = this.currentEstimate.structures;

    departmentEstimate.isAssociatedToAllStructures = true;

    const promises = structures.map(structure =>
      this.estimateStructureLinksService.createEstimateLineBasedOnStructure(
        structure,
        this.currentEstimate,
        departmentEstimate.id,
        false
      )
    );
    await Promise.all(promises);

    await this.estimateService.saveChangesAsync();
    this.currentEstimate = await this.estimateService.refreshEstimateAsync();
  }

  public filterTable(table: Table, eventTarget: EventTarget) {
    const input = eventTarget as HTMLInputElement;
    table.filterGlobal(input.value, 'contains');
  }

  private confirmDelete() {
    const promise = new Promise<boolean>(resolve => {
      this.confirmationService.confirm({
        message: this.translate.instant('ERROR.NODE_DELETE') as string,
        header: this.translate.instant('HEADER.REMOVE_NODE') as string,
        icon: 'fas fa-exclamation-triangle',
        acceptLabel: this.translate.instant('BUTTON.CONFIRM') as string,
        rejectLabel: this.translate.instant('BUTTON.CANCEL') as string,
        key: 'global-confirmation-dialog',
        accept: () => resolve(true),
        reject: () => resolve(false)
      });
    });

    return promise;
  }
}
